import React, { useMemo } from 'react';

import { GuidePeekRail } from '../../../theaWeb/guide/GuidePeekRail';
import {
  visiblePublishedGuides,
  type PublishedGuideListing,
} from '../../../theaWeb/guide/publishedGuides';
import { usePublishedGuideIndex } from '../../../theaWeb/guide/usePublishedGuides';
import { useGuidePreviewDoc } from '../../../theaWeb/guide/useGuidePreviewDoc';
import {
  GUIDES_INDEX_PATH,
  useGiftGuideHubLink,
} from '../../../theaWeb/hub/giftGuideHubFlag';
import { useHubFrame } from '../../../theaWeb/hub/HubFrameContext';

/**
 * "Trending gift guides" — the logged-out homepage's rail of curated guide
 * cards, and its ONLY door to /gift-guides. (The nav link and the bottom tab
 * bar both gate on having >=1 board, and the desktop nav additionally hides
 * below 1024px, so before this rail a first-time mobile visitor had no path to
 * the browse page at all.)
 *
 * The rail draws from the SAME season-filtered published index the browse
 * page reads, so there is no featured list to maintain and nothing that can
 * drift — but it no longer shows a plain leading slice. Newly published guides
 * take negative display orders, so in practice the leading slice was always
 * the newest batch (Oct 2026: four jewelry + four style guides) while every
 * in-season guide — Halloween hosting, cozy fall books — sat out of reach.
 * `mixSeasonalGuides` interleaves the in-season guides into the evergreen
 * lead (Kate, 2026-10-19). With nothing in season it IS the leading slice.
 *
 * WHERE THE GUIDES COME FROM (#496): the published index in Firestore, via
 * `usePublishedGuideIndex` + `visiblePublishedGuides` — not the compiled-in
 * registry. Publishing, reordering or unpublishing a guide moves both
 * surfaces with no frontend deploy.
 *
 * WHAT MOVED (THE-436): the scroller — track, desktop paging arrows, card
 * width formulas, reduced-motion gate — lives in the shared `GuidePeekRail`,
 * because the recipient-board rail needs identical behaviour and Thea
 * parameterizes one component rather than forking it. This file keeps exactly
 * what is HOMEPAGE-specific: which guides (the seasonal mix), the copy, and
 * the `homepage_rail` analytics.
 */

/** How many guides the rail shows: 7 in-season + 7 evergreen while both are
 *  available. Measured 2026-10-19 in Chrome: the tiles' native
 *  `loading="lazy"` fetches nothing until the rail nears the viewport, then
 *  only the cards on screen (4 of 36 tiles at 375px, 10 of 36 at 1440px), so
 *  the cards past the first few cost nothing until swiped to. (#790 found
 *  native lazy unreliable in other horizontal scrollers; if that shows up
 *  here, move the card to useInViewportOnce.) */
export const HOME_RAIL_COUNT = 14;

/**
 * The rail's guides: in-season guides alternated with evergreen ones,
 * seasonal first, until `count` is reached.
 *
 * `visible` must already be `visiblePublishedGuides` output — season-filtered
 * and in display order — so every guide with a `season` here is in season
 * today. Both halves keep DISPLAY ORDER, which is the curation lever: Kate
 * reorders the rail by editing `displayOrder` (thea-serverless
 * `scripts/guides.py`), with no frontend deploy. When one side runs out the
 * other fills the rest, so off-season the rail is exactly the leading slice
 * of /gift-guides.
 */
export function mixSeasonalGuides(
  visible: PublishedGuideListing[],
  count: number,
): PublishedGuideListing[] {
  const seasonal = visible.filter((listing) => listing.season);
  const evergreen = visible.filter((listing) => !listing.season);

  const mixed: PublishedGuideListing[] = [];
  for (let i = 0; mixed.length < count && (i < seasonal.length || i < evergreen.length); i += 1) {
    if (i < seasonal.length) mixed.push(seasonal[i]);
    if (mixed.length < count && i < evergreen.length) mixed.push(evergreen[i]);
  }
  return mixed;
}

/** GA `entry_point` marking a click as coming from this rail rather than
 *  /gift-guides. Deliberately NOT a new `surface` value — that vocabulary is
 *  fixed (board | liked | shared_board | gift_guide). */
export const HOME_RAIL_ENTRY_POINT = 'homepage_rail';

const GUIDES_HREF = GUIDES_INDEX_PATH;

export interface HomeGuideRailProps {
  /** Injectable clock for the seasonal-guide window (tests/stories); defaults
   *  to now. Mirrors GiftGuidesPage's `now`. */
  now?: Date;
  /** Story/test injection for the peek-strip photos. When supplied the
   *  Firestore read is skipped entirely, so stories never touch Firebase. */
  previewImagesOverride?: Record<string, string[]>;
  /** Deterministic story fixtures; production always reads the published index. */
  listingsOverride?: PublishedGuideListing[];
  /** Override the number of guides shown (default HOME_RAIL_COUNT). */
  count?: number;
  /**
   * THE-1161 (rides the Gift Guide hub gate; no preview switch of its own) — defaults to
   * `false` so every existing story/test and real visitor keeps rendering
   * today's production layout when the hub gate is off. `LandingPage.tsx`
   * resolves the flag once and passes it down. ANDed with `hubFrame` below
   * (not substituted for it): this rail's wide gutter has never been
   * reachable outside the hub frame, and the new flag narrows that further
   * rather than replacing the existing gate.
   */
  wideGutterEnabled?: boolean;
}

export const HomeGuideRail: React.FC<HomeGuideRailProps> = ({
  now,
  previewImagesOverride,
  listingsOverride,
  count = HOME_RAIL_COUNT,
  wideGutterEnabled = false,
}) => {
  // Resolve once per mount — the seasonal window is a calendar-day gate, not
  // something that needs to flip live under an open tab (the same call
  // GiftGuidesPage makes, which is what keeps the two surfaces in step).
  const index = usePublishedGuideIndex(listingsOverride !== undefined);
  const listings = useMemo(
    () =>
      mixSeasonalGuides(
        visiblePublishedGuides(listingsOverride ?? index.data ?? [], now ?? new Date()),
        count,
      ),
    [listingsOverride, index.data, now, count],
  );

  const livePreviews = useGuidePreviewDoc(!!previewImagesOverride);
  const previewImages = previewImagesOverride ?? livePreviews;

  // H8/H9 (THE-581). The homepage is a legitimate landing spot for a cohort
  // link, and both of this rail's doors leave it: "See all" goes to the index
  // and every card goes into a guide. Bare hrefs here would drop the param on
  // a tester's first click off the homepage. `carryHubParam` is the identity
  // function without the param, so normal traffic renders identical hrefs.
  const carryHubParam = useGiftGuideHubLink();

  /* "…then the 'Trending gift guides' row as it is today (no 'See all')" —
     the People tab's share of the PRD's global "Changed — No 'See all' links."
     The row itself is unchanged; only the link goes.

     SAFE ONLY UNDER THE FRAME, and this is the reason the gate is here rather
     than the link simply being deleted: "See all" is today this page's ONLY
     link to `/gift-guides` for a first-time visitor, because both the desktop
     nav link and the bottom tab bar gate on having a board. Under the frame
     that is no longer true — the frame renders the tab bar unconditionally, so
     Gift guides is one tap away from every visitor — and the rail's heading is
     itself a link to the same index either way. */
  const hubFrame = useHubFrame();

  return (
    <GuidePeekRail
      listings={listings}
      previewImages={previewImages}
      headingText="Trending gift guides"
      headingHref={carryHubParam(GUIDES_HREF)}
      headingId="home-guide-rail-heading"
      hideSeeAll={hubFrame}
      // THE-1081: "every section title the same size," matching the hub's
      // own 26/28px heading register. Gated on the same `hubFrame` read as
      // hideSeeAll above so flag-off stays byte-identical and the board
      // rail / /gift-guides (this same component, different callers) keep
      // their existing 28/40px size.
      matchHubHeading={hubFrame}
      // THE-1161: this component renders on the SAME route for a first-time
      // (no-boards) visitor whose Coming Up strip / nav already read
      // PAGE_GUTTER_DESKTOP once the hub frame is active (LandingPage.tsx,
      // the !showSignedInLayout branch) — leaving this rail on its old fixed
      // 64px/1280px-cap gutter would give that one page two different left
      // edges, depending only on which section she is looking at. Board rail
      // and every other GuidePeekRail caller leave this unset.
      //
      // ANDed with `wideGutterEnabled` (giftGuideHubFlag.ts, Kate
      // 2026-10-27): `hubFrame` alone is not a safe production gate for
      // this PR's new geometry — it is a SEPARATE, also-not-generally-
      // released switch, and relying on it alone would mean flipping
      // hubFrame on (independently, for unrelated reasons) silently ships
      // this gutter redesign before Kate has reviewed it at a preview link.
      wideGutter={hubFrame && wideGutterEnabled}
      // THE-870: match the hub's "More gift guides" card (name under photo,
      // shadow) on both the homepage and the hub's People tab, which render
      // this same rail — the two rows are meant to read as one system.
      useCollageCard
      prevLabel="Previous gift guides"
      nextLabel="Next gift guides"
      entryPoint={HOME_RAIL_ENTRY_POINT}
      hrefFor={(listing) => carryHubParam(listing.href)}
    />
  );
};

export default HomeGuideRail;
